import _ from 'lodash';

import { go, chan, put, putAsync } from 'js-csp';

import { CI_TESTING } from 'assets/js/api/environment';

const localEnv = CI_TESTING ? 'testing' : process.env.NODE_ENV;

const isPromise = (maybePromise) => _.isFunction(maybePromise.then);

// componentSubscriber creates a subscriber with a channel listening for events.
// It requires the following arguments:
//
//   componentName: A unique name used to identify the component.
//   mainCh: The main channel created by SpawnDispenser that handles all
//     information delivered through the websocket.
//   onCreate: function to be run on creation (generally, one that subscribes
//     the subscriber to the main channel). (optional)
//   onOpen: function to be run on reconnect (generally, one that subscribes
//     the subscriber to the main channel). (optional)
//   onMsg: function that runs when we receive a relevant message. Takes a
//     single argument, the partially decoded JSON message. (optional)
//   onClose: function to be run on close (e.g. push a message to the
//     subscriber's channel about there being a disconnection). (optional)
//   onKill: function to be run when the subscriber is killed (generally,
//     one that unsubscribes the subscriber from the main channel). (optional)
//
// It returns the following channel:
//   killswitch: A js-csp channel which kills the process upon receiving
//     any signal. The caller can use putAsync to deliver a kill message to
//     this channel.
// }
const componentSubscriber = (
  mainCh, componentName, onMsg, onCreate, onOpen,
  onClose, onKill,
) => {
  // Init.
  let onMsgLocal = () => {};
  if (onMsg) onMsgLocal = onMsg;
  let onCreateLocal = () => {};
  if (onCreate) onCreateLocal = onCreate;
  let onOpenLocal = () => {};
  if (onOpen) onOpenLocal = onOpen;
  let onCloseLocal = () => {};
  if (onClose) onCloseLocal = onClose;
  let onKillLocal = () => {};
  if (onKill) onKillLocal = onKill;

  onCreateLocal();

  const subCh = chan();
  const killswitch = chan();

  // Add the subscriber in the main channel.
  if (!mainCh) throw Error('undefined parent channel to add subscriber to');
  if (mainCh.isClosed()) {
    throw Error('parent channel is closed; the parent is probably not ' +
    'instantiated');
  }
  putAsync(mainCh, {
    componentName,
    subscribe: true,
    channel: subCh,
    killswitch,
  });

  // Goroutine handling the killswitch.
  go(function* chHandler() {
    yield killswitch;
    onKillLocal();
    if (!subCh.isClosed()) yield put(subCh, 'kill');
    killswitch.close();
  });

  // Goroutine handling messages from the main channel.
  try {
    go(function* chHandler() {
      for (;;) {
        const value = yield subCh;
        switch (value) {
          case 'kill': {
            // Drop the subscriber from the main channel.
            if (!mainCh.isClosed()) {
              yield put(mainCh, {
                componentName,
                subscribe: true,
                delete: true,
              });
            }
            subCh.close();
            return;
          }
          // We've logged in successfully.
          case 'bridge': {
            onOpenLocal();
            break;
          }
          case 'close': {
            onCloseLocal();
            break;
          }
          // Execute the function passed to us.
          default: {
            const handle = async () => {
              const handling = onMsgLocal(value.payload);
              if (handling && isPromise(handling)) {
                await handling;
              }
              if (value.done) value.done(handling);
            };
            handle();
            break;
          }
        }
      }
    });
  } catch (err) {
    console.warn(`channel loop failure on component subscriber: ${err}`);
  }

  return killswitch;
};

// SpawnDispenser spawns a parent dispenser for handling message transmission
// from parent to child.
export const SpawnDispenser = (parentName) => {
  // Subscribers are child components in the form:
  //  componentName:
  //    channel: js-csp channel to receive messages,
  //    killswitch: js-csp channel for closing the above channel asynchronously,
  //  }
  // The channel downstream receives messages in the parent component and
  // pipes them downstream to the child component. If the child component is
  // subscribed, it will process the messages.
  const subscribers = {};

  const mainCh = chan();

  // Main channel handler.
  try {
    go(function* chHandler() {
      for (;;) {
        const value = yield mainCh;
        switch (value) {
          case 'kill': {
            if (_.size(subscribers) > 0) {
              _.forEach(_.keys(subscribers), (componentName) => {
                const subscr = subscribers[componentName];
                if (!subscr.killswitch.isClosed()) {
                  putAsync(subscr.killswitch, 'kill');
                }
              });
            }
            mainCh.close();

            return;
          }
          default: {
            // Is this a message instructing us to add or delete a child
            // component?
            if (value.subscribe) {
              // Handle removing child.
              if (value.delete) {
                if (!subscribers[value.componentName]) {
                  if (localEnv !== 'production') {
                    console.warn('dispense can not find child component ' +
                      `${value.componentName} to rm`);
                  }
                } else {
                  // The abstracted main channel handler object, passed to the
                  // child, will handle calling unsubscribe, so we can do the
                  // killing off of the subscribed child component here.
                  putAsync(subscribers[value.componentName].killswitch, true);
                  delete subscribers[value.componentName];
                }
              } else {
                // Handle adding subscriber.
                if (_.isUndefined(value.componentName)) {
                  if (localEnv !== 'production') {
                    console.warn('dispense component name is not defined');
                  }
                }
                if (subscribers[value.componentName]) {
                  if (localEnv !== 'production') {
                    console.warn('dispense overwrote component ' +
                      `named ${value.componentName} for parent ` +
                      `component ${parentName}; ` +
                      'use a unique name for the child');
                  }
                }
                subscribers[value.componentName] = {
                  channel: value.channel,
                  killswitch: value.killswitch,
                };
              }

              break;
            }

            // Handle component specific messages.
            if (!value.componentName) {
              if (localEnv !== 'production') {
                console.warn('dispense msg sent has no target! value:',
                  value);
              }
            }

            // Find a subscribed component for the message and push them to the
            // subscriber.
            if (subscribers[value.componentName]) {
              const subscr = subscribers[value.componentName];
              if (!subscr.channel.isClosed()) {
                yield put(subscr.channel, {
                  payload: value.value,
                  done: value.done,
                });
              }
            }

            break;
          }
        }
      }
    });
  } catch (err) {
    console.warn(`subscriber loop failure on component subscriber: ${err}`);
  }

  return {
    name: () => parentName,
    subscribers,
    teardown: () => {
      try {
        putAsync(mainCh, 'kill');
      } catch (err) {
        if (localEnv !== 'production') {
          console.warn('failed to close dispenser parent channel: ' +
            `${err} (dispenser name: ${parentName})`);
        }
      }
    },
    subscribe: (componentName, onMsg) => {
      componentSubscriber(mainCh, componentName, onMsg);
    },
    transmit: (componentName, val) => {
      let resolver;
      const transmissionPromise = new Promise((resolve) => {
        resolver = resolve;
      });
      putAsync(mainCh, {
        componentName,
        value: val,
        done: resolver,
      });

      return transmissionPromise;
    },
    unsubscribe: (componentName) => {
      putAsync(mainCh, {
        componentName,
        subscribe: true,
        delete: true,
      });
    },
  };
};
